import {
  WorkspaceWithBilling,
  AdminUser,
  Package,
} from "../../../interfaces/billing";

// Action Types
export const adminActionTypes = {
  // Workspaces
  GET_ADMIN_WORKSPACES: "admin/GET_ADMIN_WORKSPACES",
  GET_ADMIN_WORKSPACES_SUCCESS: "admin/GET_ADMIN_WORKSPACES_SUCCESS",
  GET_ADMIN_WORKSPACES_ERROR: "admin/GET_ADMIN_WORKSPACES_ERROR",
  GRANT_PACKAGE: "admin/GRANT_PACKAGE",
  GRANT_PACKAGE_SUCCESS: "admin/GRANT_PACKAGE_SUCCESS",
  GRANT_PACKAGE_ERROR: "admin/GRANT_PACKAGE_ERROR",
  UPDATE_WORKSPACE_PACKAGE: "admin/UPDATE_WORKSPACE_PACKAGE",
  UPDATE_WORKSPACE_PACKAGE_SUCCESS: "admin/UPDATE_WORKSPACE_PACKAGE_SUCCESS",
  UPDATE_WORKSPACE_PACKAGE_ERROR: "admin/UPDATE_WORKSPACE_PACKAGE_ERROR",
  REVOKE_PACKAGE: "admin/REVOKE_PACKAGE",
  REVOKE_PACKAGE_SUCCESS: "admin/REVOKE_PACKAGE_SUCCESS",
  REVOKE_PACKAGE_ERROR: "admin/REVOKE_PACKAGE_ERROR",
  // Users
  GET_ADMIN_USERS: "admin/GET_ADMIN_USERS",
  GET_ADMIN_USERS_SUCCESS: "admin/GET_ADMIN_USERS_SUCCESS",
  GET_ADMIN_USERS_ERROR: "admin/GET_ADMIN_USERS_ERROR",
  UPDATE_ADMIN_USER: "admin/UPDATE_ADMIN_USER",
  UPDATE_ADMIN_USER_SUCCESS: "admin/UPDATE_ADMIN_USER_SUCCESS",
  UPDATE_ADMIN_USER_ERROR: "admin/UPDATE_ADMIN_USER_ERROR",
  // Reuses UPDATE_ADMIN_USER_SUCCESS/_ERROR (merge-by-id) for its result.
  UPDATE_ADMIN_USER_ROLE: "admin/UPDATE_ADMIN_USER_ROLE",
  DELETE_ADMIN_USER: "admin/DELETE_ADMIN_USER",
  DELETE_ADMIN_USER_SUCCESS: "admin/DELETE_ADMIN_USER_SUCCESS",
  DELETE_ADMIN_USER_ERROR: "admin/DELETE_ADMIN_USER_ERROR",
  // Packages
  GET_ADMIN_PACKAGES: "admin/GET_ADMIN_PACKAGES",
  GET_ADMIN_PACKAGES_SUCCESS: "admin/GET_ADMIN_PACKAGES_SUCCESS",
  GET_ADMIN_PACKAGES_ERROR: "admin/GET_ADMIN_PACKAGES_ERROR",
  CREATE_ADMIN_PACKAGE: "admin/CREATE_ADMIN_PACKAGE",
  CREATE_ADMIN_PACKAGE_SUCCESS: "admin/CREATE_ADMIN_PACKAGE_SUCCESS",
  CREATE_ADMIN_PACKAGE_ERROR: "admin/CREATE_ADMIN_PACKAGE_ERROR",
  UPDATE_ADMIN_PACKAGE: "admin/UPDATE_ADMIN_PACKAGE",
  UPDATE_ADMIN_PACKAGE_SUCCESS: "admin/UPDATE_ADMIN_PACKAGE_SUCCESS",
  UPDATE_ADMIN_PACKAGE_ERROR: "admin/UPDATE_ADMIN_PACKAGE_ERROR",
  DELETE_ADMIN_PACKAGE: "admin/DELETE_ADMIN_PACKAGE",
  DELETE_ADMIN_PACKAGE_SUCCESS: "admin/DELETE_ADMIN_PACKAGE_SUCCESS",
  DELETE_ADMIN_PACKAGE_ERROR: "admin/DELETE_ADMIN_PACKAGE_ERROR",
  // General
  CLEAR_ADMIN_ERROR: "admin/CLEAR_ADMIN_ERROR",
  // Impersonation
  IMPERSONATE_USER_REQUEST: "admin/IMPERSONATE_USER_REQUEST",
  STOP_IMPERSONATION_REQUEST: "admin/STOP_IMPERSONATION_REQUEST",
};

// State Interface
interface AdminState {
  workspaces: WorkspaceWithBilling[];
  workspacesMeta: {
    current_page: number;
    last_page: number;
    per_page: number;
    total: number;
  } | null;
  workspacesLoading: boolean;
  users: AdminUser[];
  usersMeta: {
    current_page: number;
    last_page: number;
    per_page: number;
    total: number;
  } | null;
  usersLoading: boolean;
  packages: Package[];
  packagesMeta: {
    current_page: number;
    last_page: number;
    per_page: number;
    total: number;
  } | null;
  packagesLoading: boolean;
  error: string | null;
}

const initialState: AdminState = {
  workspaces: [],
  workspacesMeta: null,
  workspacesLoading: false,
  users: [],
  usersMeta: null,
  usersLoading: false,
  packages: [],
  packagesMeta: null,
  packagesLoading: false,
  error: null,
};

// Reducer
export const adminReducer = (state = initialState, action: any): AdminState => {
  switch (action.type) {
    // Workspaces
    case adminActionTypes.GET_ADMIN_WORKSPACES:
    case adminActionTypes.GRANT_PACKAGE:
    case adminActionTypes.REVOKE_PACKAGE:
      return { ...state, workspacesLoading: true, error: null };

    case adminActionTypes.GET_ADMIN_WORKSPACES_SUCCESS:
      return {
        ...state,
        workspacesLoading: false,
        workspaces: action.payload.data,
        workspacesMeta: action.payload.meta || null,
      };

    case adminActionTypes.GRANT_PACKAGE_SUCCESS:
    case adminActionTypes.REVOKE_PACKAGE_SUCCESS:
      return {
        ...state,
        workspacesLoading: false,
        workspaces: state.workspaces.map((ws) =>
          ws.id === action.payload.id ? action.payload : ws
        ),
      };

    case adminActionTypes.GET_ADMIN_WORKSPACES_ERROR:
    case adminActionTypes.GRANT_PACKAGE_ERROR:
    case adminActionTypes.REVOKE_PACKAGE_ERROR:
      return { ...state, workspacesLoading: false, error: action.payload };

    // Users
    case adminActionTypes.GET_ADMIN_USERS:
    case adminActionTypes.UPDATE_ADMIN_USER:
    case adminActionTypes.UPDATE_ADMIN_USER_ROLE:
    case adminActionTypes.DELETE_ADMIN_USER:
      return { ...state, usersLoading: true, error: null };

    case adminActionTypes.GET_ADMIN_USERS_SUCCESS:
      return {
        ...state,
        usersLoading: false,
        users: action.payload.data,
        usersMeta: action.payload.meta || null,
      };

    case adminActionTypes.UPDATE_ADMIN_USER_SUCCESS:
      return {
        ...state,
        usersLoading: false,
        users: state.users.map((user) =>
          user.id === action.payload.id ? action.payload : user
        ),
      };

    case adminActionTypes.DELETE_ADMIN_USER_SUCCESS:
      return {
        ...state,
        usersLoading: false,
        users: state.users.filter((user) => user.id !== action.payload),
      };

    case adminActionTypes.GET_ADMIN_USERS_ERROR:
    case adminActionTypes.UPDATE_ADMIN_USER_ERROR:
    case adminActionTypes.DELETE_ADMIN_USER_ERROR:
      return { ...state, usersLoading: false, error: action.payload };

    // Packages
    case adminActionTypes.GET_ADMIN_PACKAGES:
    case adminActionTypes.CREATE_ADMIN_PACKAGE:
    case adminActionTypes.UPDATE_ADMIN_PACKAGE:
    case adminActionTypes.DELETE_ADMIN_PACKAGE:
      return { ...state, packagesLoading: true, error: null };

    case adminActionTypes.GET_ADMIN_PACKAGES_SUCCESS:
      return {
        ...state,
        packagesLoading: false,
        packages: action.payload.data,
        packagesMeta: action.payload.meta || null,
      };

    case adminActionTypes.CREATE_ADMIN_PACKAGE_SUCCESS:
      return {
        ...state,
        packagesLoading: false,
        packages: [...state.packages, action.payload],
      };

    case adminActionTypes.UPDATE_ADMIN_PACKAGE_SUCCESS:
      return {
        ...state,
        packagesLoading: false,
        packages: state.packages.map((pkg) =>
          pkg.id === action.payload.id ? action.payload : pkg
        ),
      };

    case adminActionTypes.DELETE_ADMIN_PACKAGE_SUCCESS:
      return {
        ...state,
        packagesLoading: false,
        packages: state.packages.filter((pkg) => pkg.id !== action.payload),
      };

    case adminActionTypes.GET_ADMIN_PACKAGES_ERROR:
    case adminActionTypes.CREATE_ADMIN_PACKAGE_ERROR:
    case adminActionTypes.UPDATE_ADMIN_PACKAGE_ERROR:
    case adminActionTypes.DELETE_ADMIN_PACKAGE_ERROR:
      return { ...state, packagesLoading: false, error: action.payload };

    // General
    case adminActionTypes.CLEAR_ADMIN_ERROR:
      return { ...state, error: null };

    default:
      return state;
  }
};

// Actions
export const adminActions = {
  // Workspaces
  setWorkspaces: (data: { data: WorkspaceWithBilling[]; meta?: any }) => ({
    type: adminActionTypes.GET_ADMIN_WORKSPACES_SUCCESS,
    payload: data,
  }),
  grantPackageSuccess: (workspace: WorkspaceWithBilling) => ({
    type: adminActionTypes.GRANT_PACKAGE_SUCCESS,
    payload: workspace,
  }),
  revokePackageSuccess: (workspace: WorkspaceWithBilling) => ({
    type: adminActionTypes.REVOKE_PACKAGE_SUCCESS,
    payload: workspace,
  }),
  // Users
  setUsers: (data: { data: AdminUser[]; meta?: any }) => ({
    type: adminActionTypes.GET_ADMIN_USERS_SUCCESS,
    payload: data,
  }),
  updateUserSuccess: (user: AdminUser) => ({
    type: adminActionTypes.UPDATE_ADMIN_USER_SUCCESS,
    payload: user,
  }),
  deleteUserSuccess: (id: number) => ({
    type: adminActionTypes.DELETE_ADMIN_USER_SUCCESS,
    payload: id,
  }),
  // Packages
  setPackages: (data: { data: Package[]; meta?: any }) => ({
    type: adminActionTypes.GET_ADMIN_PACKAGES_SUCCESS,
    payload: data,
  }),
  createPackageSuccess: (pkg: Package) => ({
    type: adminActionTypes.CREATE_ADMIN_PACKAGE_SUCCESS,
    payload: pkg,
  }),
  updatePackageSuccess: (pkg: Package) => ({
    type: adminActionTypes.UPDATE_ADMIN_PACKAGE_SUCCESS,
    payload: pkg,
  }),
  deletePackageSuccess: (id: number) => ({
    type: adminActionTypes.DELETE_ADMIN_PACKAGE_SUCCESS,
    payload: id,
  }),
  // Errors
  setError: (error: string) => ({
    type: adminActionTypes.GET_ADMIN_WORKSPACES_ERROR,
    payload: error,
  }),
  clearError: () => ({ type: adminActionTypes.CLEAR_ADMIN_ERROR }),
};

// Trigger Actions
export const adminTriggerActions = {
  // Workspaces
  getWorkspaces: (params?: { perPage?: number; page?: number; search?: string }) => ({
    type: adminActionTypes.GET_ADMIN_WORKSPACES,
    params,
  }),
  grantPackage: (data: { workspace_id: number; plan_id?: number; start_date: string; expires_at: string }) => ({
    type: adminActionTypes.GRANT_PACKAGE,
    data,
  }),
  updateWorkspacePackage: (data: { workspace_id: number; start_date: string; expires_at: string }) => ({
    type: adminActionTypes.UPDATE_WORKSPACE_PACKAGE,
    data,
  }),
  revokePackage: (workspaceId: number) => ({
    type: adminActionTypes.REVOKE_PACKAGE,
    workspaceId,
  }),
  // Users
  getUsers: (params?: { perPage?: number; page?: number; search?: string }) => ({
    type: adminActionTypes.GET_ADMIN_USERS,
    params,
  }),
  updateUser: (data: {
    id: number;
    first_name?: string;
    last_name?: string;
    email?: string;
    /** Per-user offline support switch (super admin user management). */
    offline_enabled?: boolean;
  }) => ({
    type: adminActionTypes.UPDATE_ADMIN_USER,
    data,
  }),
  updateUserWorkspaceRole: (data: { id: number; workspace_id: number; role: string }) => ({
    type: adminActionTypes.UPDATE_ADMIN_USER_ROLE,
    data,
  }),
  deleteUser: (id: number) => ({
    type: adminActionTypes.DELETE_ADMIN_USER,
    id,
  }),
  // Packages
  getPackages: (params?: { perPage?: number; page?: number }) => ({
    type: adminActionTypes.GET_ADMIN_PACKAGES,
    params,
  }),
  createPackage: (data: any) => ({
    type: adminActionTypes.CREATE_ADMIN_PACKAGE,
    data,
  }),
  updatePackage: (data: { id: number } & Partial<Package>) => ({
    type: adminActionTypes.UPDATE_ADMIN_PACKAGE,
    data,
  }),
  deletePackage: (id: number) => ({
    type: adminActionTypes.DELETE_ADMIN_PACKAGE,
    id,
  }),
  // Impersonation
  impersonateUser: (userId: number, workspaceId?: number, emergencyReason?: string) => ({
    type: adminActionTypes.IMPERSONATE_USER_REQUEST,
    userId,
    workspaceId,
    emergencyReason,
  }),
  stopImpersonation: () => ({
    type: adminActionTypes.STOP_IMPERSONATION_REQUEST,
  }),
};
